import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";
import { fetchOrderLanguage, fetchOrderCountry } from "../Services/apiService";
import { PDF_FONT, registerPdfFont } from "./pdfFont";

// Function to determine report language based on country
const getLanguageFromCountry = (country) => {
  console.log(`🔍 getLanguage called with country:`, country);
  console.log(`🔍 Country type:`, typeof country);

  if (!country) {
    console.log(`⚠️ No country provided, defaulting to english`);
    return "english";
  }

  const countryLower = country.toLowerCase();
  console.log(`🔍 Country lowercase:`, countryLower);

  if (
    countryLower.includes("france") ||
    countryLower.includes("french") ||
    countryLower === "fr"
  ) {
    console.log(`🇫🇷 Detected French language for country: ${country}`);
    return "french";
  } else if (
    countryLower.includes("germany") ||
    countryLower.includes("german") ||
    countryLower.includes("deutsch") ||
    countryLower === "de"
  ) {
    console.log(`🇩🇪 Detected German language for country: ${country}`);
    return "german";
  } else if (
    countryLower.includes("austria") ||
    countryLower.includes("austrian") ||
    countryLower === "at"
  ) {
    console.log(`🇦🇹 Detected Austrian language for country: ${country}`);
    return "austrian";
  } else if (
    countryLower.includes("netherlands") ||
    countryLower.includes("dutch") ||
    countryLower === "nl"
  ) {
    console.log(`🇳🇱 Netherlands/Dutch detected for country: ${country}`);
    return "dutch";
  } else {
    console.log(`🇬🇧 Defaulting to English language for country: ${country}`);
    return "english";
  }
};

const normalizeApiLanguage = (value) => {
  if (!value || typeof value !== "string") return "english";

  const lower = value.toLowerCase().trim();

  if (
    lower.includes("france") ||
    lower.includes("french") ||
    lower === "fr"
  ) {
    return "french";
  }

  if (
    lower.includes("germany") ||
    lower.includes("german") ||
    lower.includes("deutsch") ||
    lower === "de"
  ) {
    return "german";
  }

  if (
    lower.includes("austria") ||
    lower.includes("austrian") ||
    lower === "at"
  ) {
    return "austrian";
  }

  if (
    lower.includes("netherlands") ||
    lower.includes("dutch") ||
    lower === "nl"
  ) {
    return "dutch";
  }

  return "english";
};

const resolveTestLanguage = async (test, languageCache) => {
  const orderId = test.order_number;

  if (orderId && languageCache[orderId]) {
    return languageCache[orderId];
  }

  if (orderId) {
    try {
      const response = await fetchOrderLanguage(orderId);
      const languageValue =
        response?.order_language ||
        response?.language ||
        response?.data?.order_language ||
        response?.data?.language ||
        response;
      const normalized = normalizeApiLanguage(languageValue);

      languageCache[orderId] = normalized;
      console.log(
        `🗣️ Resolved language from API: ${normalized} for order: ${orderId}`
      );
      return normalized;
    } catch (error) {
      console.error(
        `❌ Failed to fetch language for order ${orderId}, falling back to country:`,
        error
      );
    }
  }

  const fallbackLanguage = getLanguageFromCountry(test.customer_country);
  if (orderId) {
    languageCache[orderId] = fallbackLanguage;
  }
  return fallbackLanguage;
};

// The return label is only included for orders shipping to Germany —
// this must be driven by the order's country, not its selected language.
const isGermanyCountry = (value) => {
  if (!value || typeof value !== "string") return false;
  const lower = value.toLowerCase().trim();
  return lower === "de" || lower.includes("germany") || lower.includes("deutschland");
};

const resolveTestIsGermany = async (test, countryCache) => {
  const orderId = test.order_number;

  if (orderId && countryCache[orderId] !== undefined) {
    return countryCache[orderId];
  }

  if (orderId) {
    try {
      const response = await fetchOrderCountry(orderId);
      const countryValue =
        response?.country ||
        response?.order_country ||
        response?.data?.country ||
        response?.data?.order_country ||
        response;
      const isGermany = isGermanyCountry(countryValue);

      countryCache[orderId] = isGermany;
      console.log(
        `🌍 Resolved country from API: ${countryValue} (isGermany: ${isGermany}) for order: ${orderId}`
      );
      return isGermany;
    } catch (error) {
      console.error(
        `❌ Failed to fetch country for order ${orderId}, falling back to customer_country:`,
        error
      );
    }
  }

  const fallbackIsGermany = isGermanyCountry(test.customer_country);
  if (orderId) {
    countryCache[orderId] = fallbackIsGermany;
  }
  return fallbackIsGermany;
};

// Translation function
const getTranslations = (language) => {
  const translations = {
    english: {
      instructionsTitle: "Important Instructions for the Test",
      impInstruction1:
        "Please scan the QR code to access the test instructions and the information form.",
      impInstruction2:
        "Submitting the completed form is required to receive your test results.",
      impInstruction3: "Return the collected samples in the original box.",
      impInstruction4:
        "Customers outside Germany are responsible for the return shipping costs.",
      packageContents: "Package Contents:",
      tenSamplingTubes: "10 Sampling tubes",
      oneMouthpiece: "1 Mouthpiece",
      oneTestSubstrate: "1 Test substrate",
      tenLabelsForTheTubes: "10 Sample Labels",
      oneReturnLabel: "1 Return label",
      sampleLabelsQt: "10 Sample labels",
      testExpiry: "Test must be performed within 4 months of reception.",
      observeExpiry: "Please also observe the date of expiry.",
      bestUsedBefore: "Substrat best before: See packaging",
      tubeExpiry: "Test tubes valid till 02.06.27",
      warningsTitle: "Warnings and Storage Instructions",
      warning1: "Keep out of reach of children.",
      warning2: "Store in a dry place at room temperature.",
      warning3: "Recommended for individuals aged 16 and over.",
      returnAddress: "Return Address: Sibolab UG, Zehdenicker Straße 7A, 10119 Berlin",
      assembledBy: "Assembled by Sibolab UG",

      footertext: "Assembled by Sibolab UG, Zehdenicker Str. 7A, 10119 Berlin, Germany",

      sampleText: "Sample",
    },
    french: {
      instructionsTitle: "Informations importantes pour le test",
      impInstruction1:
        "Veuillez scanner le code QR pour accéder aux instructions du test et au formulaire d’informations personnelles",
      impInstruction2:
        "L’envoi du formulaire complété est indispensable pour recevoir vos résultats.",
      impInstruction3:
        "Veuillez renvoyer les échantillons prélevés dans la boîte d’origine.",
      impInstruction4:
        "Les clients hors d’Allemagne sont responsables des frais de retour.",
      packageContents: "Contenu de l’emballage:",
      tenSamplingTubes: "10 tubes d’échantillonnage",
      oneMouthpiece: "1 embout buccal",
      oneTestSubstrate: "1 substrat de test",
      tenLabelsForTheTubes: "10 Étiquettes d’échantillons",
      oneReturnLabel: "1 étiquette de retour",
      testExpiry:
        "Le test doit être réalisé dans les 4 mois suivant la réception.",
      observeExpiry: "Veuillez également respecter la date de péremption.",
      bestUsedBefore: "Expiration du substrat: Voir l'emballage",
      tubeExpiry: "Tubes valables jusqu'au 02.06.27",
      warningsTitle: "Consignes de sécurité et de stockage",
      warning1: "Conserver hors de portée des enfants.",
      warning2: "À stocker dans un endroit sec, à température ambiante.",
      warning3: "Recommandé à partir de 16 ans.",
      returnAddress: "Adresse de retour: Sibolab UG, Zehdenicker Straße 7A, 10119 Berlin",
      assembledBy: "Compilé par Sibolab UG",

      footertext: "Compilé par Sibolab UG, Zehdenicker Str. 7A, 10119 Berlin, Allemagne",
      

      sampleText: "Échantillon"
    },
    german: {
      instructionsTitle: "Wichtige Hinweise zum Atemgastest",
      impInstruction1:
        "Bitte scannen Sie den QR-Code, um die Testanleitung sowie das Auskunftsformular zu öffnen.",
      impInstruction2:
        "Die Übermittlung des Auskunftsformulars ist Voraussetzung für die spätere Befundübermittlung.",
      impInstruction3:
        "Senden Sie die entnommenen Proben im Originalkarton an uns zurück.",
      impInstruction4:
        "Kund*innen außerhalb Deutschlands tragen die Kosten für den Rückversand selbst.",
      packageContents: "Packungsinhalt:",
      tenSamplingTubes: "10 Probenröhrchen",
      oneMouthpiece: "1 Mundstück",
      oneTestSubstrate: "1 Testsubstrat",
      tenLabelsForTheTubes: "10 Röhrchenetiketten",
      oneReturnLabel: "1 Rücksendelabel",
      testExpiry:
        "Der Test muss innerhalb von 4 Monaten nach Erhalt durchgeführt werden.",
      observeExpiry: "Bitte beachten Sie das Ablaufdatum.",
      bestUsedBefore: "Substrat verwendbar bis: Siehe Verpackung",
      tubeExpiry: "Röhrchen haltbar bis 02.06.27",
      warningsTitle: "Warn- und Lagerhinweise:",
      warning1: "Außerhalb der Reichweite von Kindern aufbewahren.",
      warning2: "Trocken und bei Raumtemperatur lagern",
      warning3: "Empfohlen für Personen ab 16 Jahren.",
      returnAddress: "Rücksendeadresse: Sibolab UG, Zehdenicker Straße 7A, 10119 Berlin",
      assembledBy: "Zusammengestellt von Sibolab UG",

      footertext: "Zusammengestellt von Sibolab UG, Zehdenicker Str. 7A, 10119 Berlin, Deutschland.",

      sampleText: "Probe"
    },
    
    austrian: {
      instructionsTitle: "Wichtige Hinweise zum Atemgastest",
      impInstruction1:
        "Bitte scannen Sie den QR-Code, um die Testanleitung sowie das Auskunftsformular zu öffnen.",
      impInstruction2:
        "Die Übermittlung des Auskunftsformulars ist Voraussetzung für die spätere Befundübermittlung.",
      impInstruction3:
        "Senden Sie die entnommenen Proben im Originalkarton an uns zurück.",
      impInstruction4:
        "Kund*innen außerhalb Deutschlands tragen die Kosten für den Rückversand selbst.",
      packageContents: "Packungsinhalt:",
      tenSamplingTubes: "10 Probenröhrchen",
      oneMouthpiece: "1 Mundstück",
      oneTestSubstrate: "1 Testsubstrat",
      tenLabelsForTheTubes: "10 Röhrchenetiketten",
      oneReturnLabel: "1 Rücksendelabel",
      testExpiry:
        "Der Test muss innerhalb von 4 Monaten nach Erhalt durchgeführt werden.",
      observeExpiry: "Bitte beachten Sie das Ablaufdatum.",
      bestUsedBefore: "Substrat verwendbar bis: Siehe Verpackung",
      tubeExpiry: "Röhrchen haltbar bis 02.06.27",
      warningsTitle: "Warn- und Lagerhinweise:",
      warning1: "Außerhalb der Reichweite von Kindern aufbewahren.",
      warning2: "Trocken und bei Raumtemperatur lagern",
      warning3: "Empfohlen für Personen ab 16 Jahren.",
      returnAddress: "Rücksendeadresse: Sibolab UG, Zehdenicker Straße 7A, 10119 Berlin",
      assembledBy: "Zusammengestellt von Sibolab UG",

      footertext: "Zusammengestellt von Sibolab UG, Zehdenicker Str. 7A, 10119 Berlin, Deutschland.",

      sampleText: "Probe"
    },
  };
  if (language === "dutch") {
    // Dutch content is not yet localized; use English copy as fallback.
    return translations.english;
  }

  return translations[language] || translations.english;
};

/**
 * Generate timestamp for filenames
 */
const getTimestamp = () => {
  const now = new Date();
  const date = now.toISOString().split("T")[0]; // YYYY-MM-DD
  const time = now.toTimeString().split(" ")[0].replace(/:/g, "-"); // HH-MM-SS
  return `${date}_${time}`;
};

/**
 * Utility: Convert Base64 PDF to Uint8Array
 */
const base64ToUint8Array = (base64) => {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/**
 * Merge multiple Base64 PDF labels (Outbound or Return) into ONE PDF using pdf-lib
 */
const downloadCombinedShippingLabelsPDF = async (labels, filename) => {
  if (!labels || labels.length === 0) return;

  const mergedPdf = await PDFDocument.create();

  for (const label of labels) {
    if (!label.label_data) continue;
    const pdfBytes = base64ToUint8Array(label.label_data);
    const pdf = await PDFDocument.load(pdfBytes);
    const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    pages.forEach((page) => mergedPdf.addPage(page));
  }

  const mergedBytes = await mergedPdf.save();
  const blob = new Blob([mergedBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * QR Stickers PDF (199x103 mm)
 */

const downloadQRStickersPDF = async (labTests, languageCache, countryCache) => {
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [199, 103],
  });
  await registerPdfFont(pdf);

  let firstPage = true;

  for (let testIndex = 0; testIndex < labTests.length; testIndex += 1) {
    const test = labTests[testIndex];
    console.log(`🏷️ Processing test ${testIndex + 1}:`, test);
    console.log(`🌍 Customer country from test:`, test.customer_country);

    if (!firstPage) pdf.addPage();
    firstPage = false;

    // ✅ Reset text color to black for each page
    pdf.setTextColor(0);

    // Get current report language from test data
    const qrLanguage = await resolveTestLanguage(test, languageCache);
    console.log(
      `🗣️ Detected language: ${qrLanguage} for order: ${test.order_number}`
    );

    // Get translations for current language
    const t = getTranslations(qrLanguage);
    console.log(`📝 Using translations:`, t);

    // Return label inclusion depends on the order's shipping country, not its language
    const isGermany = await resolveTestIsGermany(test, countryCache);

    // ✅ Expiry date (11 months from now)
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 11); // 11 months from now
    const expiryText = `${String(expiryDate.getMonth() + 1).padStart(2, "0")}/${String(expiryDate.getFullYear()).slice(-2)}`;

    // Positioning
    const leftX = 12;
    const rightX = 112;
    const topOffset = -10; // Move content up more

    // ✅ Add PNG logo (header) — preserve native aspect ratio (3840×1045)
    const qrLogoWidth = 22;
    const qrLogoHeight = qrLogoWidth / (3840 / 1045);
    pdf.addImage("/Sibolab_Logo_black_rgb.png", "PNG", 163, 8, qrLogoWidth, qrLogoHeight);

    // ✅ Left Column (QR + Test Info)
    if (test.qrcode) {
      pdf.addImage(test.qrcode, "PNG", leftX - 1.5, 6, 28, 28);
    }

    let y = 37;
    pdf.setFont(PDF_FONT, "normal").setFontSize(10);
    pdf.text(`Test ID: ${test.test_identifier}`, leftX, y);
    y += 6;

    // ✅ Remove "SIBO" prefix if present
    if (test.substrate) {
      const cleanSubstrate = test.substrate.replace(/^SIBO\s+/i, "");
      pdf.text(`Substrate: ${cleanSubstrate}`, leftX, y);
      y += 6;
    }

    // ✅ Important Instructions (wrapped)
    pdf.setFont(PDF_FONT, "bold").text(t.instructionsTitle, leftX, y);
    y += 5;
    pdf.setFont(PDF_FONT, "normal").setFontSize(9);
    const instructions = [
      t.impInstruction1,
      t.impInstruction2,
      t.impInstruction3,
      t.impInstruction4,
    ];
    const wrappedInstructions = pdf.splitTextToSize(instructions, 85); // keep in col 1 width (increased from 80 to 85)
    wrappedInstructions.forEach((line) => {
      pdf.text(line, leftX, y);
      y += 4;
    });

    // ✅ Add Return Address to left column (bold)
    y += 2;
    pdf.setFont(PDF_FONT, "bold").setFontSize(9);
    const returnAddressLines = pdf.splitTextToSize(t.returnAddress, 85);
    let returnAddressLastLineY = y;
    returnAddressLines.forEach((line) => {
      pdf.text(line, leftX, y);
      returnAddressLastLineY = y;
      y += 4;
    });

    // ✅ Right Column (Package + Expiry + Warnings) — start aligned with QR top (y=4 image + ~4mm cap height)
    let ry = 11;
    pdf.setFont(PDF_FONT, "bold").setFontSize(10);
    pdf.text(t.packageContents, rightX, ry);
    ry += 5;

    if (!isGermany) {
      pdf.setFont(PDF_FONT, "normal").setFontSize(9);
      [
        t.tenSamplingTubes,
        t.oneMouthpiece,
        t.oneTestSubstrate,
        t.tenLabelsForTheTubes,
      ].forEach((item) => {
        if (item && typeof item === "string" && !isNaN(rightX) && !isNaN(ry)) {
          pdf.text(item, rightX, ry);
          ry += 4;
        }
      });
    } else {
      pdf.setFont(PDF_FONT, "normal").setFontSize(9);
      [
        t.tenSamplingTubes,
        t.oneMouthpiece,
        t.oneTestSubstrate,
        t.tenLabelsForTheTubes,
        t.oneReturnLabel,
      ].forEach((item) => {
        if (item && typeof item === "string" && !isNaN(rightX) && !isNaN(ry)) {
          pdf.text(item, rightX, ry);
          ry += 4;
        }
      });
    }

    ry += 5;
    const wrappedObserveExpiry = pdf.splitTextToSize(t.observeExpiry, 75);
    wrappedObserveExpiry.forEach((line) => {
      pdf.text(line, rightX, ry);
      ry += 4;
    });
    ry += 1;

    // ✅ hourglass Icon instead of ⧗
    ry += 3;
    pdf.addImage("/hourglass.png", "PNG", rightX, ry, 8, 8); // Add hourglass icon (place hourglass.png in /public)
    // Note: expiryText is available but not displayed on the label
    pdf.setFont(PDF_FONT, "normal").setFontSize(9);
    pdf.text(t.bestUsedBefore, rightX + 12, ry + 3);
    pdf.text(t.tubeExpiry, rightX + 12, ry + 7);
    ry += 14;
    ry += 3;
    ry += 4; // space freed by removing testExpiry line

    // ✅ Warnings
    pdf
      .setFont(PDF_FONT, "bold")
      .setFontSize(10)
      .text(t.warningsTitle, rightX, ry);
    ry += 5;
    pdf.setFont(PDF_FONT, "normal").setFontSize(9);
    const warnings = [t.warning1, t.warning2, t.warning3];
    const wrappedWarnings = pdf.splitTextToSize(warnings, 75);
    wrappedWarnings.forEach((line) => {
      pdf.text(line, rightX, ry);
      ry += 4;
    });

    ry += 4;

    // ✅ Add Assembled By aligned with last line of returnAddress
    pdf.setFont(PDF_FONT, "normal").setFontSize(9).setTextColor(0);
    const assembledByLines = pdf.splitTextToSize(t.assembledBy, 75);
    let assembledByDrawY = returnAddressLastLineY;
    assembledByLines.forEach((line) => {
      pdf.text(line, rightX, assembledByDrawY);
      assembledByDrawY += 4;
    });
  }

  pdf.save(`QR Labels ${getTimestamp()}.pdf`);
};

/**
 * Test Tube Stickers PDF (32x19 mm)
 */
const downloadTestTubeStickersPDF = async (labTests, languageCache) => {
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [32, 19],
  });
  await registerPdfFont(pdf);
  let firstPage = true;

  for (const test of labTests) {
    // Get current report language from API (fallback to country)
    const qrLanguage = await resolveTestLanguage(test, languageCache);
    const t = getTranslations(qrLanguage);

    for (const tag of test.sample_tags || []) {
      if (!firstPage) pdf.addPage();
      firstPage = false;

      // Extract Sample number (last part after 2nd dash)
      const parts = tag.split("-");
      const sampleNumber = parts.length >= 3 ? parts[2] : "";

      // Page dimensions
      const pageWidth = 32;

      // Define 3 rows layout with closer spacing
      const row1Y = 6.5; // First row - Test ID
      const row2Y = 10; // Second row - Sample number  
      const row3Y = 14; // Third row - Logo

      // Font sizes
      const testIdFontSize = 10;
      const sampleFontSize = 9;

      // Row 1: Test ID (centered)
      pdf.setFont(PDF_FONT, "bold").setFontSize(testIdFontSize);
      const testIdText = test.test_identifier;
      const testIdWidth = pdf.getTextWidth(testIdText);
      pdf.text(testIdText, (pageWidth - testIdWidth) / 2, row1Y);

      // Row 2: Sample text (centered)
      pdf.setFont(PDF_FONT, "normal").setFontSize(sampleFontSize);
      const sampleText = `${t.sampleText} ${sampleNumber}`;
      const sampleWidth = pdf.getTextWidth(sampleText);
      pdf.text(sampleText, (pageWidth - sampleWidth) / 2, row2Y);

      // Row 3: Logo (centered) - made bigger
      const logoWidth = 10;
      const logoHeight = logoWidth / (3840 / 1045);
      pdf.addImage("/Sibolab_Logo_black_rgb.png", "PNG", 
        (pageWidth - logoWidth) / 2, 
        row3Y - logoHeight / 2, 
        logoWidth, 
        logoHeight
      );
    }
  }

  pdf.save(`Sample Stickers ${getTimestamp()}.pdf`);
};

/**
 * MAIN: Generate 4 PDFs:
 * 1) Outbound Labels PDF (merged)
 * 2) Return Labels PDF (merged)
 * 3) QR Stickers PDF
 * 4) Sample Stickers PDF
 */
export const downloadAllOrderPDFs = async (orderData) => {
  if (!orderData || !orderData.data) throw new Error("No order data");

  const allLabTests = [];
  const allOutbound = [];
  const allReturns = [];
  const languageCache = {};
  const countryCache = {};

  // Extract data
  orderData.data.forEach((orderItem) => {
    const orderNum = orderItem.order?.order_number || orderItem.order?.name;
    const customerCountry = orderItem.order?.customer_country || "Unknown";

    // Lab tests - include country information
    (orderItem.lab_tests || []).forEach((test) => {
      allLabTests.push({
        ...test,
        order_number: orderNum,
        customer_country: customerCountry,
      });
    });

    // Outbound label
    if (orderItem.shipping_label?.outbound) {
      allOutbound.push(orderItem.shipping_label.outbound);
    }

    // Return labels
    if (orderItem.shipping_label?.return) {
      const returns = Array.isArray(orderItem.shipping_label.return)
        ? orderItem.shipping_label.return
        : [orderItem.shipping_label.return];
      allReturns.push(...returns);
    }
  });

  console.log(
    `Orders: ${orderData.data.length}, Lab Tests: ${allLabTests.length}, Outbound: ${allOutbound.length}, Returns: ${allReturns.length}`
  );

  // Generate PDFs
  if (allOutbound.length)
    await downloadCombinedShippingLabelsPDF(
      allOutbound,
      `Outbound Labels ${getTimestamp()}.pdf`
    );
  if (allReturns.length)
    await downloadCombinedShippingLabelsPDF(
      allReturns,
      `Return Labels ${getTimestamp()}.pdf`
    );

  if (allLabTests.length)
    await downloadQRStickersPDF(allLabTests, languageCache, countryCache);
  if (allLabTests.length)
    await downloadTestTubeStickersPDF(allLabTests, languageCache);

  console.log("✅ All PDFs generated successfully!");
};
